import * as React from "react";
import * as _ from "lodash";
import styled, { css } from "styled-components";
import * as queryString from "query-string";
import { useLocation } from "react-router-dom";

import Grid from "src/library/components/Hedron";
import { Text } from "src/library/components/Text/Text";
import Dropdown from "src/library/components/Dropdown/Dropdown";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import {
  IProductCategory,
  useProductsApi
} from "src/core/products/api/products.api";
import { useDrop } from "src/core/drops/hooks";
import useOrder from "src/core/orders/hooks/useOrder";

interface ProductCategoryFilterProps {
  handleSearch: (categoriesFilter: { categories: { id: number | null, name: string | null } }) => void;
}

const ProductCategoryFilter: React.ReactFCWithChildren<ProductCategoryFilterProps> = ({ handleSearch }) => {
  const [ isOpen, setIsOpen ] = React.useState(false);
  const [ listProductCategoriesResponse, listProductCategories ] = useProductsApi("LIST_PRODUCT_CATEGORIES");
  const [ parentCategory, setParentCategory ] = React.useState<IProductCategory | null>(null);
  const [ selectedCategory, setSelectedCategory ] = React.useState<IProductCategory | null>(null);
  const [ hasLoadedInitial, setHasLoadedInitial ] = React.useState(false);
  const location = useLocation();
  const [ { drop } ] = useDrop();
  const [ { currentOrder } ] = useOrder();

  const handleInitialCategory = React.useCallback(() => {
    if (!hasLoadedInitial) {
      if (location.search) {
        const params = queryString.parse(location.search);

        if (params && params.category) {
          const categoryId = parseInt(params.category as string, 10);

          if (typeof categoryId === "number") {
            const newCategory = _.find(listProductCategoriesResponse.data, category => category.id === categoryId);
            const hasChildren = _.find(listProductCategoriesResponse.data, category => category.parentId === categoryId);

            if (hasChildren && newCategory) {
              setSelectedCategory(newCategory || null);
              setParentCategory(newCategory);
            }
          }
        }
      }

      setHasLoadedInitial(true);
    }
  }, [ selectedCategory ]);

  // get all product categories
  React.useEffect(() => {
    if (drop) {
      if (!hasLoadedInitial) {
        listProductCategories({
          filters: currentOrder.data?.orderDeliveryDate ? { orderDate: currentOrder.data?.orderDeliveryDate } : {},
          branchId: drop.id
        });
      }

      if (!hasLoadedInitial) {
        handleInitialCategory();
      }
    }
  }, [
    listProductCategories,
    handleInitialCategory,
    hasLoadedInitial,
    drop,
    currentOrder.data?.orderDeliveryDate
  ]);

  const onCategoryClick = React.useCallback(
    (newCategory: IProductCategory | null, showAll = false) => {
      setSelectedCategory(newCategory || null);

      if (!showAll) {
        if (newCategory) {
          const hasChildren = _.find(listProductCategoriesResponse.data, category => category.parentId === newCategory.id);

          if (hasChildren) {
            setParentCategory(newCategory);

            return;
          }
        } else {
          setParentCategory(null);

          return;
        }
      }

      handleSearch({
        categories: {
          id: newCategory?.id || null,
          name: newCategory?.name || null
        }
      });
      // close modal
      setIsOpen(false);
    },
    [ listProductCategoriesResponse.data, handleSearch ]
  );

  const childrenCategories = React.useMemo(() => {
    let childCategories: IProductCategory[] = [];

    if (!listProductCategoriesResponse.data) {
      return childCategories;
    }

    if (parentCategory) {
      if (parentCategory.parentId) {
        childCategories = _.filter(listProductCategoriesResponse.data, category => category.parentId === parentCategory.parentId);
      } else {
        childCategories = _.filter(listProductCategoriesResponse.data, category => category.parentId === parentCategory.id);
      }

      return _.orderBy(childCategories, cat => cat.name);
    }

    return _.orderBy(_.filter(listProductCategoriesResponse.data, category => category.parentId === null), cat => cat.sortOrder);
  }, [ parentCategory, listProductCategoriesResponse.data ]);

  return (
    <Dropdown
      isLoading={listProductCategoriesResponse.pending}
      isOpen={isOpen}
      toggleOpen={newIsOpen => setIsOpen(typeof newIsOpen === "boolean" ? newIsOpen : !isOpen)}
      title={selectedCategory ? selectedCategory.name : "Category"}
      DropdownInnerComponent={() => (
        <DropdownInner direction="vertical">
          {parentCategory ? (
            <HeaderItem
              className="has-icon"
              onClick={() => onCategoryClick(null)}
            >
              <Icon icon={Icons.CHEVRONLEFT} />

              <Text weight="bold">
                Back to categories
              </Text>
            </HeaderItem>
          ) : (
            <HeaderItem onClick={() => onCategoryClick(null, true)}>
              <Text weight="bold">
                View all
              </Text>
            </HeaderItem>
          )}

          {!!parentCategory && (
            <HeaderItem onClick={() => onCategoryClick(parentCategory, true)}>
              <Text weight="bold">
                {`View all ${parentCategory.name}`}
              </Text>
            </HeaderItem>
          )}

          {_.map(childrenCategories, category => (
            <CategoryItem
              key={`category-${category.id}`}
              className={`${selectedCategory && category.id === selectedCategory.id && "active"}`}
              onClick={() => onCategoryClick(category)}
            >
              <Text className="category-text">
                {category.name}
              </Text>
            </CategoryItem>
          ))}
        </DropdownInner>
      )}
    />
  );
};

const DropdownInner = styled(Grid.Bounds)`
  width: 200px;
`;

const CategoryItem = styled(Grid.Box)`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.sm};
    transition: ${theme.transition.quickest};
    &:hover {
      cursor: pointer;
      background: ${theme.colors.lightGrey};
    }

    &.active {
      .category-text {
        color: ${theme.colors.primaryDark};
        font-weight: ${theme.fonts.weights.bold};
      }
    }
  `}
`;

const HeaderItem = styled(Grid.Box)`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md};
    border-bottom: 1px solid ${theme.colors.lightGrey};
    transition: ${theme.transition.quickest};
    &:hover {
      cursor: pointer;
      background: ${theme.colors.lightGrey};
    }

    &.has-icon {
      position: relative;
      padding-left: 30px;

      svg {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        left: 0;
      }
    }
  `}
`;

export default ProductCategoryFilter;
